import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import clsx from 'clsx';

import { useOutsideClick } from '@/hooks/ui/useOutsideClick';
import {
  fetchProjects,
  fetchSmartProcessTypes,
  Project,
} from '@/entities/task/api/projects';
import { fetchDeals } from '@/entities/task/api/сrm';
import { fetchCrmLeads, type CrmLead } from '@/lib/api/lead';
import type { Deal } from '@/shared/types/crm';
import {
  DocumentsPanel,
  formatDocDate,
  getExtension,
  getKindFromExt,
  type PanelCategory,
  type PanelDoc,
} from '@/shared/ui/documents';
import { FloatingPanel } from '@/shared/ui/FloatingPanel';
import { useProjectCardSummary, useProjectData } from '@/entities/project-card';
import {
  useTaskDocuments,
  type TaskDocCategory,
} from '@/entities/task/api/documents';
import { useDebounced } from '@/hooks/ui/useDebounced';
import { entityTypeToProduct, onAltArrowDownOpen, uiText } from '../utils';
import type {
  SmartProcessTypeUI,
  SmartProcessOption,
  ProjectOption,
  PreviewFile,
} from '../types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ANY = any;

/* ─── Light searchable select (funnel / project) ─── */
function LightSelect({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  mobile,
  serverMode,
  onQueryChange,
  loading,
}: {
  value: string;
  onChange: (id: string) => void;
  options: { id: string; title: string }[];
  placeholder: string;
  disabled?: boolean;
  mobile?: boolean;
  /** Поиск на сервере: клиентскую фильтрацию отключаем, ввод отдаём наружу. */
  serverMode?: boolean;
  onQueryChange?: (q: string) => void;
  loading?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  useOutsideClick({
    enabled: open,
    refs: [btnRef as ANY, panelRef as ANY],
    onOutside: () => setOpen(false),
  });

  const selected = options.find((o) => o.id === value);

  const filtered = useMemo(() => {
    if (serverMode) {
      return options;
    }
    const s = q.trim().toLowerCase();
    if (!s) {
      return options;
    }
    return options.filter((o) => o.title.toLowerCase().includes(s));
  }, [options, q, serverMode]);

  // Серверный поиск: пробрасываем ввод наружу (родитель дебаунсит и грузит).
  useEffect(() => {
    if (serverMode) {
      onQueryChange?.(q);
    }
  }, [q, serverMode, onQueryChange]);

  useEffect(() => {
    if (!open) {
      setQ('');
      return;
    }
    const t = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  return (
    <div className="relative w-full">
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onAltArrowDownOpen(open, () => setOpen(true))}
        className={clsx(
          'flex h-[48px] w-full items-center justify-between gap-3 rounded-[16.98px] bg-[#F8F7FC] px-[14px]',
          'shadow-[0_3.54px_12.88px_rgba(0,0,0,0.03)] border border-white/40',
          'text-[14px] leading-[130%] tracking-[-0.83px] text-[#14222C]',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
        aria-expanded={open}
      >
        <span className="truncate">{selected?.title ?? placeholder}</span>
        {mobile ? (
          <ChevronDown
            className={clsx(
              'h-[18px] w-[18px] shrink-0 text-[#000000] transition',
              open && 'rotate-180'
            )}
          />
        ) : (
          <Search className="h-[19px] w-[19px] shrink-0 text-[#6E6E6E]" />
        )}
      </button>

      {open && !disabled ? (
        <FloatingPanel
          ref={panelRef}
          anchorRef={btnRef}
          align="stretch"
          sideOffset={4}
          className="rounded-[12px] border border-black/10 bg-white p-2 shadow-[0_12px_30px_rgba(0,0,0,0.18)]"
        >
          <div className="mb-1 flex items-center gap-2 border-b border-black/10 px-2 py-1.5">
            <Search className="h-3.5 w-3.5 shrink-0 text-black/40" />
            <input
              ref={searchRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Поиск..."
              className="w-full bg-transparent text-[13px] text-[#14222C] outline-none placeholder:text-black/40"
            />
          </div>
          <div className="max-h-[260px] overflow-auto">
            {loading ? (
              <div className="px-3 py-2 text-[13px] text-black/40">
                Загрузка...
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-3 py-2 text-[13px] text-black/40">
                Ничего не найдено
              </div>
            ) : (
              filtered.map((o) => {
                const active = o.id === value;
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => {
                      onChange(o.id);
                      setOpen(false);
                    }}
                    className={clsx(
                      'w-full rounded-[8px] px-3 py-2 text-left text-[13px]',
                      active
                        ? 'bg-[#57B7921A] text-[#14222C]'
                        : 'text-[#14222C]/80 hover:bg-black/5'
                    )}
                  >
                    {o.title}
                  </button>
                );
              })
            )}
          </div>
        </FloatingPanel>
      ) : null}
    </div>
  );
}

/* ─── Result card pieces ─── */

/** RFC3339/ISO → dd.mm.yyyy. Пустое → ''; уже нормализованную дату не трогаем. */
function fmtDate(v: unknown): string {
  const s = uiText(v).trim();
  if (!s) {
    return '';
  }
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(s)) {
    return s;
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) {
    return s;
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

// Мини-превью картинок через авторизованный прокси (как на табе «Проект»).
const THUMB_SIZE = 96;
function thumbUrlFor(item: PanelDoc): string | undefined {
  const ext = getExtension(item.name);
  if (ext === 'svg' || getKindFromExt(ext) !== 'image') {
    return undefined;
  }
  return `/files/${encodeURIComponent(String(item.id))}/raw?thumb=${THUMB_SIZE}`;
}

/* ─── project → превью-строки (те же данные, что на табе «Проект» задачи) ─── */
type ContactRow = {
  id: string | number;
  name: string;
  position: string;
  phone: string;
};

function contactName(c: ANY): string {
  const last = c?.lastName ?? c?.LastName ?? c?.last_name;
  const direct = c?.name ?? c?.fullName ?? c?.FullName;
  if (direct) {
    const lastStr = uiText(last);
    const d = String(direct).trim();
    return lastStr && !d.includes(lastStr) ? `${lastStr} ${d}`.trim() : d;
  }
  const first = c?.firstName ?? c?.FirstName ?? c?.first_name;
  const second = c?.secondName ?? c?.SecondName ?? c?.second_name;
  return [last, first, second].map(uiText).filter(Boolean).join(' ').trim();
}

function contactPhone(c: ANY): string {
  if (typeof c?.phone === 'string' && c.phone) {
    return c.phone;
  }
  const phones = c?.phones ?? c?.PHONE ?? c?.Phones;
  if (Array.isArray(phones) && phones.length > 0) {
    const f = phones[0];
    if (typeof f === 'string') {
      return f;
    }
    if (f && typeof f === 'object') {
      return String(f.value ?? f.VALUE ?? f.number ?? '');
    }
  }
  return '';
}

function mapContacts(raw: unknown): ContactRow[] {
  const list = Array.isArray(raw) ? (raw as ANY[]) : [];
  return list
    .map((c, i) => ({
      id: c?.id ?? c?.ID ?? i + 1,
      name: contactName(c),
      position: uiText(c?.post ?? c?.position ?? c?.Post ?? c?.workPosition),
      phone: contactPhone(c),
    }))
    .filter((c) => c.name || c.position || c.phone);
}

function normFiles(value: unknown): PreviewFile[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((x: ANY): PreviewFile | null => {
      if (!x || typeof x !== 'object') {
        return null;
      }
      const idRaw = x.id ?? x.ID;
      const id =
        typeof idRaw === 'number'
          ? idRaw
          : typeof idRaw === 'string' && idRaw.trim()
            ? Number(idRaw)
            : NaN;
      const name = typeof x.name === 'string' ? x.name : '';
      const downloadUrl =
        typeof x.downloadUrl === 'string'
          ? x.downloadUrl
          : typeof x.url === 'string'
            ? x.url
            : '';
      if (!Number.isFinite(id) || !name) {
        return null;
      }
      return { id, name, downloadUrl };
    })
    .filter((x): x is PreviewFile => Boolean(x));
}

type DocSection = { key: string; title: string; files: PreviewFile[] };

function dedupeFiles(files: PreviewFile[]): PreviewFile[] {
  const seen = new Set<string>();
  return files.filter((f) => {
    const k = String(f.id);
    if (seen.has(k)) {
      return false;
    }
    seen.add(k);
    return true;
  });
}

function fileFieldsOf(
  customFields: unknown
): { label: string; files: PreviewFile[] }[] {
  const list = Array.isArray(customFields) ? (customFields as ANY[]) : [];
  return list
    .filter((f) => String(f?.type ?? '').toLowerCase() === 'file')
    .map((f) => ({
      label: String(f?.label ?? '').toLowerCase(),
      files: normFiles(f?.value),
    }));
}

const isTz = (label: string) => /(техничес|тз|тех)/.test(label);
const isContract = (label: string) => label.includes('договор');

/** Секции документов проекта (как на табе «Проект»): файлы из разложенных
 *  бэком categorizedFiles + file-поля customFields, разнесённые по
 *  ТЗ / Договоры / Прочее. */
function buildDocSections(
  cardFiles: Record<string, ANY[]> | undefined,
  customFields: unknown
): DocSection[] {
  const fields = fileFieldsOf(customFields);
  const tz = dedupeFiles([
    ...normFiles(cardFiles?.tz),
    ...fields.filter((f) => isTz(f.label)).flatMap((f) => f.files),
  ]);
  const contract = dedupeFiles([
    ...normFiles(cardFiles?.contract),
    ...fields.filter((f) => isContract(f.label)).flatMap((f) => f.files),
  ]);
  const other = dedupeFiles([
    ...normFiles(cardFiles?.other),
    ...fields
      .filter((f) => !isTz(f.label) && !isContract(f.label))
      .flatMap((f) => f.files),
  ]);
  return [
    { key: 'sec-tz', title: 'Технические задания', files: tz },
    {
      key: 'sec-contract',
      title: 'Договоры / Коммерческие предложения',
      files: contract,
    },
    { key: 'sec-other', title: 'Прочее', files: other },
  ];
}

function ContactsPanel({ contacts }: { contacts: ContactRow[] }) {
  return (
    <div>
      <div className="mb-1 text-[14px] leading-[130%] tracking-[-0.83px] text-[#575757]">
        Контакты
      </div>
      {contacts.length === 0 ? (
        <div className="rounded-[8px] border border-[rgba(43,43,43,0.12)] bg-white px-2 py-2 text-[14px] text-black/45">
          Контактов нет
        </div>
      ) : (
        <div className="rounded-[8px] border border-[rgba(43,43,43,0.12)] bg-white px-3 py-2">
          <div className="grid grid-cols-3 gap-2 pb-1 text-[12px] tracking-[-0.83px] text-[rgba(0,0,0,0.5)]">
            <span>Контакт</span>
            <span>Должность</span>
            <span>Номер телефона</span>
          </div>
          <div className="flex flex-col gap-1">
            {contacts.map((c) => (
              <div
                key={String(c.id)}
                className="grid grid-cols-3 gap-2 text-[14px] tracking-[-0.83px] text-[#14222C]"
              >
                <span className="min-w-0 truncate" title={c.name}>
                  {c.name || '—'}
                </span>
                <span className="min-w-0 truncate" title={c.position}>
                  {c.position || '—'}
                </span>
                <span className="min-w-0 truncate">{c.phone || '—'}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Секция карточки → категория серверной ручки документов задачи.
const SECTION_TO_CAT: Record<string, TaskDocCategory> = {
  'sec-tz': 'tz',
  'sec-contract': 'contract',
  'sec-other': 'other',
};

function ResultCard({
  data,
  mobile,
  docsTaskId,
  currentBinding,
}: {
  data: Project;
  mobile?: boolean;
  /** id задачи, из которой открыта модалка — включает серверный список
   *  документов (как на табе «Проект», с вложениями самой задачи). */
  docsTaskId?: string;
  /** Текущая привязка задачи: серверные документы показываем только когда
   *  выбран именно этот элемент (у другого элемента файлов задачи нет). */
  currentBinding?: { entityTypeId: number; id: number } | null;
}) {
  // Контакты и файлы полей карточки в /projects/list не приходят — тянем их
  // отдельной ручкой /project-cards/{entityTypeId}/{id} (те же данные, что на
  // табе «Проект» задачи).
  const summaryQ = useProjectCardSummary(data.entityTypeId, data.id);
  const summary = summaryQ.data;

  // Файлы задач проекта (папки «Doit / Final», «Правки» и т.п. — как на
  // детальной странице проекта, таб «Данные»): без этой ручки предпросмотр
  // показывал только 3 фиксированные CRM-категории и терял основную массу
  // документов, реально прикреплённых к задачам проекта.
  const dataQ = useProjectData(data.entityTypeId, data.id, 'all', true);

  const contacts = useMemo(
    () => mapContacts(summary?.contacts ?? data?.contacts),
    [summary, data]
  );

  const sections = useMemo(() => {
    const base = buildDocSections(
      (summary as ANY)?.categorizedFiles ?? data?.categorizedFiles,
      summary?.customFields ?? data?.customFields
    );
    const taskGroups = dataQ.data?.taskFiles ?? [];
    const taskSections: DocSection[] = taskGroups
      .map((g) => ({
        key: `task-${g.taskId}`,
        title: g.taskTitle || `Задача #${g.taskId}`,
        files: normFiles(g.files),
      }))
      .filter((s) => s.files.length > 0);
    return [...base, ...taskSections];
  }, [summary, data, dataQ.data]);

  const [cat, setCat] = useState('');
  const [catOpen, setCatOpen] = useState(false);
  const [docSearch, setDocSearch] = useState('');
  const activeKey = sections.some((s) => s.key === cat)
    ? cat
    : (sections[0]?.key ?? '');
  const active = sections.find((s) => s.key === activeKey) ?? null;
  const needle = docSearch.trim().toLowerCase();
  const activeFiles = (active?.files ?? []).filter(
    (f) => !needle || f.name.toLowerCase().includes(needle)
  );

  // Серверный режим — только для текущей привязки задачи и только для трёх
  // фиксированных CRM-категорий (для них есть серверная ручка документов
  // задачи); папки по задачам проекта показываем локально из /data?source=all.
  const serverMode = Boolean(
    docsTaskId &&
    currentBinding &&
    data.entityTypeId === currentBinding.entityTypeId &&
    data.id === currentBinding.id &&
    SECTION_TO_CAT[activeKey]
  );
  const debouncedSearch = useDebounced(docSearch, 400);
  const docsQuery = useTaskDocuments(
    docsTaskId,
    SECTION_TO_CAT[activeKey],
    serverMode ? debouncedSearch.trim() : '',
    serverMode
  );
  const serverDocs = docsQuery.data?.pages.flatMap((p) => p.data) ?? [];

  const items: PanelDoc[] = serverMode
    ? serverDocs.map((d) => ({
        id: d.id,
        name: d.name,
        date: d.createdAt ? formatDocDate(d.createdAt) : undefined,
      }))
    : activeFiles.map((f) => ({ id: f.id, name: f.name }));
  const categories: PanelCategory[] = sections.map((s) => ({
    key: s.key,
    title: s.title,
    count: serverMode ? undefined : s.files.length,
  }));

  const description =
    uiText(summary?.lastMessage) || uiText(data?.resume) || 'Нет описания';

  const dates = [
    { label: 'Заявка поступила', value: data?.deal?.requestApplication },
    { label: 'Договор заключен', value: data?.deal?.contractDate },
    { label: 'Начало проекта', value: data?.dateStart },
    { label: 'Сдача проекта', value: data?.dateFinish },
  ];

  const manager = (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-[14px] tracking-[-0.83px] text-[rgba(0,0,0,0.5)]">
        Ответственный менеджер
      </span>
      <span className="text-[14px] tracking-[-0.83px] text-[#14222C]">
        {uiText(data?.deal?.assignedByName) || 'Не назначен'}
      </span>
    </div>
  );

  return (
    <div className="mt-[16px] rounded-[16.98px] border border-black/5 bg-white px-5 py-3 shadow-[0_3.54px_12.88px_rgba(0,0,0,0.03)]">
      <div className="flex items-baseline justify-between gap-4">
        <div className="flex min-w-0 items-baseline gap-2">
          <div className="truncate text-[18px] leading-[130%] tracking-[-0.83px] text-[#14222C]">
            {uiText(data?.title) || ''}
          </div>
          <div className="text-[14px] leading-[130%] tracking-[-0.83px] text-[rgba(20,34,44,0.5)]">
            {uiText(data?.company?.Industry) || ''}
          </div>
        </div>
        {!mobile ? (
          <div className="flex shrink-0 items-baseline gap-2 text-right">
            <span className="text-[14px] tracking-[-0.83px] text-[rgba(0,0,0,0.5)]">
              Ответственный менеджер
            </span>
            <span className="text-[14px] tracking-[-0.83px] text-[#14222C]">
              {uiText(data?.deal?.assignedByName) || 'Не назначен'}
            </span>
          </div>
        ) : null}
      </div>

      <div className="mt-2 text-[14px] leading-[130%] tracking-[-0.83px] text-[rgba(0,0,0,0.5)]">
        {description}
      </div>

      {mobile ? (
        <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3">
          {dates.map((x) => (
            <div key={x.label}>
              <div className="text-[12px] leading-[130%] tracking-[-0.83px] text-[#575757]">
                {x.label}
              </div>
              <div className="text-[12px] leading-[130%] tracking-[-0.83px] text-[#14222C]">
                {fmtDate(x.value) || '—'}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-4 flex flex-wrap items-start gap-y-3">
          {dates.map((x, idx) => (
            <div key={x.label} className="flex items-start">
              <div className="pr-[18px]">
                <div className="text-[14px] leading-[130%] tracking-[-0.83px] text-[#575757]">
                  {x.label}
                </div>
                <div className="text-[14px] leading-[130%] tracking-[-0.83px] text-[#14222C]">
                  {fmtDate(x.value) || '—'}
                </div>
              </div>
              {idx !== dates.length - 1 ? (
                <div className="mr-[18px] h-[36px] w-px bg-black/10" />
              ) : null}
            </div>
          ))}
        </div>
      )}

      {mobile ? <div className="mt-4">{manager}</div> : null}

      <div className="mt-4">
        <ContactsPanel contacts={contacts} />
      </div>

      <div className="mt-4">
        <div className="mb-1 text-[14px] leading-[130%] tracking-[-0.83px] text-[#575757]">
          Документы
        </div>
        <DocumentsPanel
          className="h-[360px]"
          categories={categories}
          category={activeKey}
          onCategoryChange={setCat}
          catOpen={catOpen}
          onCatOpenChange={setCatOpen}
          items={items}
          search={docSearch}
          onSearchChange={setDocSearch}
          selectedId={null}
          getThumbUrl={thumbUrlFor}
          onSelect={(item) => {
            const url = serverMode
              ? serverDocs.find((x) => x.id === item.id)?.downloadUrl
              : activeFiles.find((x) => x.id === item.id)?.downloadUrl;
            if (url) {
              window.open(url, '_blank', 'noopener');
            }
          }}
          loading={
            serverMode
              ? docsQuery.isLoading
              : summaryQ.isLoading || dataQ.isLoading
          }
          hasMore={serverMode && docsQuery.hasNextPage}
          onLoadMore={serverMode ? () => docsQuery.fetchNextPage() : undefined}
          loadingMore={serverMode && docsQuery.isFetchingNextPage}
          emptyText="Файлов нет"
        />
      </div>
    </div>
  );
}

/** Вид привязки, выбранный в первом дропдауне. */
type BindingKind = 'smart' | 'deal' | 'lead' | null;

/** Предвыбранная привязка для авто-подтягивания (из UF_CRM_TASK). */
export type InitialBinding =
  | { kind: 'smart'; entityTypeId: number; id: number }
  | { kind: 'deal'; id: number }
  | { kind: 'lead'; id: number }
  | null;

/** Первая распознанная привязка из UF_CRM_TASK: "T{e}_{id}" / "D_{id}" / "L_{id}". */
export function parseInitialBinding(
  ufCrmTask: string[] | undefined
): InitialBinding {
  for (const b of ufCrmTask ?? []) {
    const s = String(b);
    const smart = /^T(\d+)_(\d+)$/.exec(s);
    if (smart) {
      return {
        kind: 'smart',
        entityTypeId: Number(smart[1]),
        id: Number(smart[2]),
      };
    }
    const deal = /^D_(\d+)$/.exec(s);
    if (deal) {
      return { kind: 'deal', id: Number(deal[1]) };
    }
    const lead = /^L_(\d+)$/.exec(s);
    if (lead) {
      return { kind: 'lead', id: Number(lead[1]) };
    }
  }
  return null;
}

/* ─── Main block ─── */
export function DSmartProcess({
  disabled,
  onApply,
  onApplyRef,
  onSelectionChange,
  mobile,
  initialBinding,
  docsTaskId,
  currentBinding,
}: {
  disabled?: boolean;
  /** Привязка смарт-процесса/проекта (формат "T{entityTypeId}_{id}"). */
  onApply?: (data: Project) => void;
  /** Привязка сырым рефом — для сделки ("D_{id}") или лида ("L_{id}"). */
  onApplyRef?: (ref: string) => void;
  /** Текущий выбор (UF_CRM_TASK-реф) при ЛЮБОЙ смене записи, не только по
   *  «Подтянуть данные». Нужно, чтобы сохранялось то, что видно на экране. */
  onSelectionChange?: (ref: string | null) => void;
  mobile?: boolean;
  /** Предвыбранная привязка (напр. при создании задачи из сообщения) —
   *  блок сам выберет тип/запись и «подтянет» данные. */
  initialBinding?: InitialBinding;
  /** id задачи (если блок открыт из существующей задачи) — серверный список
   *  документов для текущей привязки. */
  docsTaskId?: string;
  /** Текущая привязка задачи (смарт-процесс) — с чем сверять выбранный элемент
   *  для показа серверных документов. */
  currentBinding?: { entityTypeId: number; id: number } | null;
}) {
  const [types, setTypes] = useState<SmartProcessTypeUI[]>([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [typesError, setTypesError] = useState('');

  // Первый дропдаун: 'deal' | 'lead' | числовой EntityTypeID смарт-процесса.
  const [entityTypeId, setEntityTypeId] = useState<string>('');
  // Второй дропдаун: id выбранной записи (проект/сделка/лид).
  const [itemId, setItemId] = useState<string>('');

  const [projects, setProjects] = useState<Project[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [leads, setLeads] = useState<CrmLead[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsError, setItemsError] = useState('');

  // Поисковый запрос второго дропдауна — серверный поиск с дебаунсом.
  const [itemQuery, setItemQuery] = useState('');
  const debouncedItemQuery = useDebounced(itemQuery, 300);

  // Точечно резолвнутая текущая привязка (по ids) — чтобы префилл показывал
  // реальную запись, даже если её нет в текущей выдаче поиска.
  const [prefillProject, setPrefillProject] = useState<Project | null>(null);
  const [prefillDeal, setPrefillDeal] = useState<Deal | null>(null);
  const [prefillLead, setPrefillLead] = useState<CrmLead | null>(null);

  // Карточка-превью смарт-проекта + флаг «подтянуто» (для всех видов).
  const [, setPreview] = useState<Project | null>(null);
  const [pulled, setPulled] = useState(false);

  // Авто-подтягивание предвыбранной привязки — только один раз.
  const autoAppliedRef = useRef(false);

  /* --- типы смарт-процессов --- */
  useEffect(() => {
    if (disabled) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setTypesLoading(true);
        setTypesError('');
        const data = await fetchSmartProcessTypes();
        const list = Array.isArray(data?.result) ? data.result : [];
        const normalized: SmartProcessTypeUI[] = list
          .map((t: ANY) => ({
            ID: Number(t?.ID ?? 0),
            EntityTypeID: Number(t?.EntityTypeID ?? 0),
            Title: { String: String(t?.Title?.String ?? '') },
            CreatedTime: String(t?.CreatedTime ?? ''),
            TableName: String(t?.TableName ?? ''),
          }))
          .filter((t) => Number.isFinite(t.EntityTypeID) && t.EntityTypeID > 0)
          .sort((a, b) => a.Title.String.localeCompare(b.Title.String, 'ru'));
        if (!cancelled) {
          setTypes(normalized);
        }
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          setTypesError('Не удалось загрузить смарт-процессы');
        }
      } finally {
        if (!cancelled) {
          setTypesLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [disabled]);

  const selectedEntityTypeNum = useMemo(() => {
    const n = Number(entityTypeId);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [entityTypeId]);

  const kind: BindingKind =
    entityTypeId === 'deal'
      ? 'deal'
      : entityTypeId === 'lead'
        ? 'lead'
        : selectedEntityTypeNum
          ? 'smart'
          : null;

  // Первый дропдаун: сделка/лид всегда сверху, затем смарт-процессы.
  const typeOptions: SmartProcessOption[] = useMemo(
    () => [
      { id: 'deal', title: 'Сделка' },
      { id: 'lead', title: 'Лид' },
      ...types.map((t) => ({
        id: String(t.EntityTypeID),
        title: t.Title.String || entityTypeToProduct(t.EntityTypeID),
      })),
    ],
    [types]
  );

  // Смена типа — сбрасываем выбор записи, поиск и превью.
  useEffect(() => {
    setItemId('');
    setItemQuery('');
    setPreview(null);
    setPulled(false);
  }, [entityTypeId]);

  /* --- список записей второго дропдауна: серверный поиск с дебаунсом --- */
  useEffect(() => {
    if (disabled) {
      return;
    }
    setItemsError('');
    if (!kind) {
      setProjects([]);
      setDeals([]);
      setLeads([]);
      return;
    }
    const q = debouncedItemQuery.trim();
    let cancelled = false;
    (async () => {
      try {
        setItemsLoading(true);
        if (kind === 'smart' && selectedEntityTypeNum) {
          const data = await fetchProjects({
            entityTypeId: selectedEntityTypeNum,
            search: q || undefined,
            limit: 50,
          });
          const list = Array.isArray(data?.result) ? data.result : [];
          if (!cancelled) {
            setProjects(list);
          }
        } else if (kind === 'deal') {
          const data = await fetchDeals({
            page: 1,
            limit: 50,
            title: q || undefined,
          });
          const list = data?.result ?? data?.items ?? [];
          if (!cancelled) {
            setDeals(Array.isArray(list) ? list : []);
          }
        } else if (kind === 'lead') {
          const data = await fetchCrmLeads({
            search: q || undefined,
            limit: 50,
          });
          const list = Array.isArray(data?.result) ? data.result : [];
          if (!cancelled) {
            setLeads(list);
          }
        }
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          setItemsError('Не удалось загрузить список');
        }
      } finally {
        if (!cancelled) {
          setItemsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [disabled, kind, selectedEntityTypeNum, debouncedItemQuery]);

  /* --- префилл: точечно резолвим текущую привязку по ids --- */
  useEffect(() => {
    if (disabled || !initialBinding) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        if (initialBinding.kind === 'smart') {
          const data = await fetchProjects({
            entityTypeId: initialBinding.entityTypeId,
            ids: String(initialBinding.id),
          });
          const rec = data?.result?.[0];
          if (rec && !cancelled) {
            setPrefillProject(rec);
          }
        } else if (initialBinding.kind === 'deal') {
          const data = await fetchDeals({
            page: 1,
            ids: String(initialBinding.id),
          });
          const rec = (data?.result ?? data?.items)?.[0];
          if (rec && !cancelled) {
            setPrefillDeal(rec);
          }
        } else {
          const data = await fetchCrmLeads({ ids: String(initialBinding.id) });
          const rec = data?.result?.[0];
          if (rec && !cancelled) {
            setPrefillLead(rec);
          }
        }
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [disabled, initialBinding]);

  const itemOptions: ProjectOption[] = useMemo(() => {
    let opts: ProjectOption[] = [];
    let prefill: ProjectOption | null = null;
    if (kind === 'smart') {
      opts = projects.map((p) => ({ id: String(p.id), title: p.title.String }));
      if (prefillProject) {
        prefill = {
          id: String(prefillProject.id),
          title: prefillProject.title.String,
        };
      }
    } else if (kind === 'deal') {
      opts = deals.map((d) => ({
        id: String(d.ID),
        title: d.Title || `Сделка #${d.ID}`,
      }));
      if (prefillDeal) {
        prefill = {
          id: String(prefillDeal.ID),
          title: prefillDeal.Title || `Сделка #${prefillDeal.ID}`,
        };
      }
    } else if (kind === 'lead') {
      opts = leads.map((l) => ({
        id: String(l.id),
        title: l.title || `Лид #${l.id}`,
      }));
      if (prefillLead) {
        prefill = {
          id: String(prefillLead.id),
          title: prefillLead.title || `Лид #${prefillLead.id}`,
        };
      }
    }
    // Текущая привязка всегда доступна для выбора, даже если поиск её вытеснил.
    if (prefill && !opts.some((o) => o.id === prefill!.id)) {
      opts = [prefill, ...opts];
    }
    return opts;
  }, [kind, projects, deals, leads, prefillProject, prefillDeal, prefillLead]);

  const selectedProject = useMemo(() => {
    if (kind !== 'smart') {
      return null;
    }
    const pid = Number(itemId);
    if (!Number.isFinite(pid) || pid <= 0) {
      return null;
    }
    return (
      projects.find((p) => p.id === pid) ??
      (prefillProject?.id === pid ? prefillProject : null)
    );
  }, [kind, itemId, projects, prefillProject]);

  const selectedDeal = useMemo(() => {
    if (kind !== 'deal') {
      return null;
    }
    return (
      deals.find((d) => String(d.ID) === itemId) ??
      (prefillDeal && String(prefillDeal.ID) === itemId ? prefillDeal : null)
    );
  }, [kind, itemId, deals, prefillDeal]);

  const selectedLead = useMemo(() => {
    if (kind !== 'lead') {
      return null;
    }
    return (
      leads.find((l) => String(l.id) === itemId) ??
      (prefillLead && String(prefillLead.id) === itemId ? prefillLead : null)
    );
  }, [kind, itemId, leads, prefillLead]);

  // Текущий выбор → реф UF_CRM_TASK. Считаем прямо из выбранной записи (kind +
  // itemId), не дожидаясь загрузки самого объекта, чтобы сохранялось именно то,
  // что выбрано на экране (а не последний «Подтянуть»).
  useEffect(() => {
    if (!onSelectionChange) {
      return;
    }
    let ref: string | null = null;
    if (kind === 'smart' && selectedEntityTypeNum && itemId) {
      ref = `T${selectedEntityTypeNum}_${itemId}`;
    } else if (kind === 'deal' && itemId) {
      ref = `D_${itemId}`;
    } else if (kind === 'lead' && itemId) {
      ref = `L_${itemId}`;
    }
    onSelectionChange(ref);
  }, [kind, selectedEntityTypeNum, itemId, onSelectionChange]);

  const hasSelection =
    kind === 'smart' ? Boolean(selectedProject) : Boolean(itemId);
  const canPull = Boolean(kind && hasSelection) && !disabled;

  const pullData = () => {
    if (kind === 'smart') {
      if (!selectedProject) {
        return;
      }
      setPreview(selectedProject);
      setPulled(true);
      onApply?.(selectedProject);
    } else if (kind === 'deal') {
      if (!itemId) {
        return;
      }
      setPulled(true);
      onApplyRef?.(`D_${itemId}`);
    } else if (kind === 'lead') {
      if (!selectedLead) {
        return;
      }
      setPulled(true);
      onApplyRef?.(selectedLead.crmRef?.trim() || `L_${selectedLead.id}`);
    }
  };

  // Авто-подтягивание: выбрать тип предвыбранной привязки, как только известен.
  useEffect(() => {
    if (disabled || autoAppliedRef.current || !initialBinding || entityTypeId) {
      return;
    }
    if (initialBinding.kind === 'smart') {
      setEntityTypeId(String(initialBinding.entityTypeId));
    } else if (initialBinding.kind === 'deal') {
      setEntityTypeId('deal');
    } else {
      setEntityTypeId('lead');
    }
  }, [disabled, initialBinding, entityTypeId]);

  // ...и когда запись резолвнется (точечно по ids или из списка) — выбрать её.
  useEffect(() => {
    if (disabled || autoAppliedRef.current || !initialBinding) {
      return;
    }
    if (initialBinding.kind === 'smart') {
      if (
        kind !== 'smart' ||
        selectedEntityTypeNum !== initialBinding.entityTypeId
      ) {
        return;
      }
      const rec =
        projects.find((p) => p.id === initialBinding.id) ??
        (prefillProject?.id === initialBinding.id ? prefillProject : null);
      if (!rec) {
        return;
      }
      autoAppliedRef.current = true;
      setItemId(String(rec.id));
      setPreview(rec);
      setPulled(true);
      onApply?.(rec);
    } else if (initialBinding.kind === 'deal') {
      if (kind !== 'deal') {
        return;
      }
      const rec =
        deals.find((d) => d.ID === initialBinding.id) ??
        (prefillDeal?.ID === initialBinding.id ? prefillDeal : null);
      if (!rec) {
        return;
      }
      autoAppliedRef.current = true;
      setItemId(String(rec.ID));
      setPulled(true);
      onApplyRef?.(`D_${rec.ID}`);
    } else if (initialBinding.kind === 'lead') {
      if (kind !== 'lead') {
        return;
      }
      const rec =
        leads.find((l) => l.id === initialBinding.id) ??
        (prefillLead?.id === initialBinding.id ? prefillLead : null);
      if (!rec) {
        return;
      }
      autoAppliedRef.current = true;
      setItemId(String(rec.id));
      setPulled(true);
      onApplyRef?.(rec.crmRef?.trim() || `L_${rec.id}`);
    }
  }, [
    disabled,
    initialBinding,
    kind,
    selectedEntityTypeNum,
    projects,
    deals,
    leads,
    prefillProject,
    prefillDeal,
    prefillLead,
    onApply,
    onApplyRef,
  ]);

  const secondPlaceholder = !kind
    ? 'Сначала выберите тип'
    : itemsLoading
      ? 'Загрузка...'
      : kind === 'deal'
        ? 'Выбор сделки'
        : kind === 'lead'
          ? 'Выбор лида'
          : 'Выбор проекта';

  return (
    <div>
      <div className="mb-4 text-[16px] leading-[130%] tracking-[-0.03em] text-[#575757]">
        Выберите тип (смарт-процесс, сделку или лид), затем запись и нажмите на
        «Подтянуть данные»
      </div>

      <div className="flex flex-wrap items-center gap-[14px]">
        <div className="min-w-0 flex-1">
          <LightSelect
            value={entityTypeId}
            onChange={setEntityTypeId}
            options={typeOptions}
            placeholder={
              typesLoading ? 'Загрузка...' : 'Смарт-процесс / сделка / лид'
            }
            disabled={Boolean(disabled)}
            mobile={mobile}
          />
        </div>

        <div className="min-w-0 flex-1">
          <LightSelect
            value={itemId}
            onChange={(id) => {
              // Зафиксировать выбранную запись, чтобы она не пропала, когда
              // серверный поиск сменит выдачу списка.
              const nid = Number(id);
              if (kind === 'smart') {
                const r = projects.find((p) => p.id === nid);
                if (r) {
                  setPrefillProject(r);
                }
              } else if (kind === 'deal') {
                const r = deals.find((d) => String(d.ID) === id);
                if (r) {
                  setPrefillDeal(r);
                }
              } else if (kind === 'lead') {
                const r = leads.find((l) => String(l.id) === id);
                if (r) {
                  setPrefillLead(r);
                }
              }
              setItemId(id);
              setPreview(null);
              setPulled(false);
            }}
            options={itemOptions}
            placeholder={secondPlaceholder}
            disabled={Boolean(disabled) || !kind}
            mobile={mobile}
            serverMode
            onQueryChange={setItemQuery}
            loading={itemsLoading}
          />
        </div>

        <button
          type="button"
          disabled={!canPull}
          onClick={pullData}
          className={clsx(
            'h-[48px] shrink-0 rounded-[24px] px-[18px] text-[14px] leading-[130%] transition',
            !canPull
              ? 'cursor-not-allowed bg-black/5 text-black/40'
              : mobile
                ? 'bg-card-gradient text-white shadow-[0_0_2.9px_rgba(138,230,255,0.2)]'
                : pulled
                  ? 'border border-[#57B792] bg-white text-[#57B792]'
                  : 'bg-white text-[#000000] shadow-[0_3.54px_12.88px_rgba(0,0,0,0.06)] border border-black/5 hover:bg-[#f7f7f7]'
          )}
        >
          {pulled ? 'Данные подкреплены' : 'Подтянуть данные'}
        </button>
      </div>

      {typesError ? (
        <div className="mt-2 text-[12px] text-red-500">{typesError}</div>
      ) : null}
      {itemsError ? (
        <div className="mt-2 text-[12px] text-red-500">{itemsError}</div>
      ) : null}

      {selectedProject ? (
        <ResultCard
          data={selectedProject}
          mobile={mobile}
          docsTaskId={docsTaskId}
          currentBinding={currentBinding}
        />
      ) : selectedDeal ? (
        <SimpleBindingCard
          title={uiText(selectedDeal.Title) || `Сделка #${selectedDeal.ID}`}
          subtitle={uiText(selectedDeal.company?.Title)}
          note={uiText(selectedDeal.stage?.Name)}
        />
      ) : selectedLead ? (
        <SimpleBindingCard
          title={uiText(selectedLead.title) || `Лид #${selectedLead.id}`}
          note={uiText(selectedLead.statusId)}
        />
      ) : null}
    </div>
  );
}

/** Лёгкая карточка-превью для сделки/лида (у них нет проектной карточки). */
function SimpleBindingCard({
  title,
  subtitle,
  note,
}: {
  title: string;
  subtitle?: string;
  note?: string;
}) {
  return (
    <div className="mt-[16px] rounded-[16.98px] border border-black/5 bg-white px-5 py-3 shadow-[0_3.54px_12.88px_rgba(0,0,0,0.03)]">
      <div className="flex items-baseline justify-between gap-4">
        <div className="min-w-0 truncate text-[18px] leading-[130%] tracking-[-0.83px] text-[#14222C]">
          {title}
        </div>
        {note ? (
          <div className="shrink-0 text-[14px] leading-[130%] tracking-[-0.83px] text-[rgba(20,34,44,0.5)]">
            {note}
          </div>
        ) : null}
      </div>
      {subtitle ? (
        <div className="mt-1 text-[14px] leading-[130%] tracking-[-0.83px] text-[rgba(0,0,0,0.5)]">
          {subtitle}
        </div>
      ) : null}
    </div>
  );
}
